import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2Icon, ChevronRightIcon } from '@/shared/ui/icons';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { extractErrorMessage } from '@/shared/api/errors';
import { formatUsd } from '@/shared/utils/currency';
import { formatDate } from '@/shared/utils/date';
import type { ApiChargesMyItem } from '@/shared/types/api/charges';
import { useMyCharges } from '@/entities/charges';

type Props = {
  open: boolean;
  onClose: () => void;
};

type ParcelGroup = {
  key: string;
  parcel: ApiChargesMyItem['parcel'];
  items: ApiChargesMyItem[];
  total: number;
};

// Groups unpaid debt lines by parcel (order = newest line first) so the
// customer sees which parcel each service is billed against.
const groupByParcel = (items: ApiChargesMyItem[]): ParcelGroup[] => {
  const map = new Map<string, ParcelGroup>();
  for (const item of items) {
    const key = item.parcel?.id ?? '__none__';
    let group = map.get(key);
    if (!group) {
      group = { key, parcel: item.parcel, items: [], total: 0 };
      map.set(key, group);
    }
    group.items.push(item);
    group.total += Number(item.amount);
  }
  return [...map.values()];
};

export const FeaturesCargoIdDebtDialog = ({ open, onClose }: Props) => {
  const { t } = useTranslation('cargoId');
  const navigate = useNavigate();
  const query = useMyCharges(open);
  const items = query.data?.items;
  const groups = useMemo(() => groupByParcel(items ?? []), [items]);
  const total = Number(query.data?.total_debt ?? 0);

  const openParcel = useCallback(
    (id: string) => {
      onClose();
      navigate(`/parcels/${id}`);
    },
    [navigate, onClose],
  );

  return (
    <Dialog open={open} onClose={onClose} title={t('debt.title')} size="lg">
      {query.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : query.isError ? (
        <ErrorState
          message={query.error ? extractErrorMessage(query.error) : t('debt.loadError')}
          onRetry={() => void query.refetch()}
        />
      ) : total <= 0 || groups.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-8 text-center">
          <CheckCircle2Icon className="h-8 w-8 text-emerald-600" aria-hidden />
          <p className="text-base font-semibold text-emerald-700">{t('debt.none')}</p>
          <p className="max-w-xs text-sm text-emerald-700/80">{t('debt.emptyHelper')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5">
            <span className="text-sm font-medium text-red-700">{t('debt.total')}</span>
            <span className="text-lg font-bold text-red-700">{formatUsd(total)}</span>
          </div>

          <div className="-mx-1 flex max-h-[55vh] flex-col gap-3 overflow-y-auto px-1 pb-1">
            {groups.map(({ key, parcel, items: lines, total: groupTotal }) => (
              <section
                key={key}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white"
              >
                {parcel ? (
                  <button
                    type="button"
                    onClick={() => openParcel(parcel.id)}
                    className="flex w-full cursor-pointer items-center gap-2 bg-slate-50 px-3 py-2.5 text-left transition-colors hover:bg-slate-100"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-ink">
                        {parcel.name || parcel.trek_id}
                      </span>
                      {parcel.trek_id && (
                        <span className="block truncate font-mono text-xs text-ink-muted">
                          {parcel.trek_id}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-sm font-semibold text-red-700">
                      {formatUsd(groupTotal)}
                    </span>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                  </button>
                ) : (
                  <div className="flex items-center justify-between gap-2 bg-slate-50 px-3 py-2.5">
                    <span className="text-sm font-semibold text-ink">{t('debt.otherServices')}</span>
                    <span className="shrink-0 text-sm font-semibold text-red-700">
                      {formatUsd(groupTotal)}
                    </span>
                  </div>
                )}

                <ul className="divide-y divide-slate-100">
                  {lines.map((item) => (
                    <li key={item.id} className="flex items-start justify-between gap-3 px-3 py-2.5">
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-ink">
                          {t(`parcelDetail:charges.types.${item.type}`, {
                            defaultValue: item.type_label,
                          })}
                        </span>
                        <span className="block truncate text-xs text-ink-muted">
                          {item.package?.name
                            ? t('debt.packageLine', {
                                name: item.package.name,
                                date: formatDate(item.created_at),
                              })
                            : formatDate(item.created_at)}
                        </span>
                      </span>
                      <span className="shrink-0 text-sm font-medium text-ink">
                        {formatUsd(Number(item.amount))}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <p className="text-xs text-ink-muted">{t('debt.openParcelHint')}</p>
        </div>
      )}
    </Dialog>
  );
};
